<template>
  <div
    class="vp-input-box"
    :class="{ dirty }"
  >
    <Input
      v-bind="$attrs"
      :input-id="inputId"
    >
      <template #default="{ uid, value, hasError, errors, onInput: onFieldInput }">
        <VPLabel
          v-if="label"
          :for="uid"
        >
          {{ label }}
        </VPLabel>
        <Accessible v-if="accessibleLabel">
          <label :for="uid">{{ accessibleLabel }}</label>
        </Accessible>
        <input
          v-bind="$attrs"
          :id="uid"
          ref="inputRef"
          :disabled="disabled"
          class="vp-input"
          :value="value"
          :type="props.inputElementType"
          @input="onInput($event, onFieldInput)"
          @keydown="onKeyDown"
        >
        <span v-if="suffix">{{ suffix }}</span>
        <span
          v-if="hasError"
          class="text-vp-error font-bold text-xs"
        >{{ errors.join(', ') }}</span>
      </template>
    </Input>
  </div>
</template>

<script setup lang="ts">
import { Input } from 'vuniform';
import { computed, ref, inject } from 'vue';
import { useFormsStore } from 'vuniform';
import VPLabel from '@/components/form/VPLabel.vue';
import Accessible from '@/components/Accessible.vue';

const formsStore = useFormsStore();

const formId = inject('formId', '');

const inputRef = ref<HTMLInputElement>(null);

type inputType = 'text' | 'password' | 'float' | 'integer';

interface Props {
  inputId: string;
  disabled?: boolean;
  type?: inputType;
  label?: string;
  accessibleLabel?: string;
  suffix?: string;
  inputElementType?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  inputElementType: 'text',
});

const dirty = computed(() => formsStore.isFieldDirty(formId, props.inputId));

const onInput = (event: InputEvent, cb: (e: { target: { value: unknown } }) => void) => {
  const handler = inputHandlers[props.type];
  if (!handler) {
    return;
  }

  const v = handler(event.target.value);
  if (cb) {
    cb({ target: { value: v } });
  }
};

const inputHandlers: { [k: inputType]: (v: string) => unknown } = {
  text: (v: string): string => v,
  password: (v: unknown): unknown => v,
  float: (v: string): number => {
    const inputNum = v.replace(/[^0-9.]/, '');

    let inputFloat = Number.parseFloat(inputNum);
    if (isNaN(inputFloat)) {
      inputFloat = 0.0;
    }
    return inputFloat;
  },
  integer: (v: string): number => {
    const inputNum = v.replace(/[^0-9.]/, '');

    let inputInt = Number.parseInt(inputNum);
    if (isNaN(inputInt)) {
      inputInt = 0.0;
    }
    return inputInt;
  },
};

const onKeyDown = (e: KeyboardEvent) => {
  switch (props.type) {
    case 'text':
    case 'password':
      return;

    case 'float':
      if (e.key.match(/^[\d.]$/)) {
        return;
      }
      break;

    case 'integer':
      if (e.key.match(/^[\d]$/)) {
        return;
      }
  }

  if (e.key.length > 1) {
    return;
  }

  e.preventDefault();
};

defineExpose({
  inputRef,
});
</script>
